import { usePlanDetails } from '@commenda/api';
import { Badge, Button, Dropdown, Icons } from '@commenda/kakashi';
import { Currency } from '@commenda/prisma';
import { BillingHelper, CurrencyHelper } from '@commenda/utils';
import { useState } from 'react';
import { PlanModal, PlanModalStep } from './modals/PlanModal';
import { PlanCategory } from './types';

interface PlanCardProps {
  id: string;
  name: string;
  category: PlanCategory;
  price: number;
  assignedTo?: string;
  onCancelPlan?: (productId: string) => void;
  features?: string[];
  nextPaymentDue?: string;
  entitlementId?: string;
  entitlementIndex?: number;
  totalEntitlements?: number;
  originalProductId?: string;
  companyId?: number;
  subscriptionId?: string;
  onPlanDeleted?: () => void;
  billingType?: Currency;
  frequency?: string | null;
}

export const PlanCard = ({
  id,
  name,
  category,
  price,
  assignedTo,
  onCancelPlan,
  features,
  nextPaymentDue,
  entitlementId,
  entitlementIndex,
  totalEntitlements,
  originalProductId,
  companyId,
  subscriptionId,
  onPlanDeleted,
  billingType = Currency.USD,
  frequency,
}: PlanCardProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState<PlanModalStep>('details');

  const cleanId = (originalProductId || id).replace(/-\d+$/, '');
  // In the UI, a single subscription card may contain multiple plan cards, each identified by a plan ID. When a plan is purchased in multiple quantities, the plan ID remains the same. To render each unit distinctly on the frontend, suffixes like -1, -2, etc., are appended to the plan ID. However, since Chargebee does not recognize these modified IDs, a cleanId function is used to strip the suffixes and retrieve the original ID for API operations.

  const { data: planDetails } = usePlanDetails({ id: cleanId });

  const handleConfirmCancellation = (reason: string) => {
    onCancelPlan?.(cleanId);
  };

  const openModal = (step: PlanModalStep) => {
    setModalStep(step);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    // Reset to default step when closing
    setModalStep('details');
  };

  const dropdownItems = [
    {
      label: 'View plan details',
      onClick: () => openModal('details'),
    },
    {
      label: 'Cancel plan',
      onClick: () => openModal('cancel'),
    },
  ];

  return (
    <>
      <div className="p-5 rounded-lg border bg-white border-primary-200">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <div className="flex items-center gap-2">
                <h3 className="text-text-primary txt-medium-bold">
                  {name}
                  {totalEntitlements &&
                    totalEntitlements > 1 &&
                    entitlementIndex !== undefined && (
                      <span className="ml-2 text-sm font-normal text-text-tertiary">
                        ({entitlementIndex + 1}/{totalEntitlements})
                      </span>
                    )}
                </h3>
              </div>
              <Badge
                variant={BillingHelper.getPlanCategoryBadgeVariant(category)}
                size="sm"
              >
                {category}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm-regular text-text-primary">
                {CurrencyHelper.getCurrencyInfo(billingType as Currency).style}
                {price}
                {BillingHelper.getBillingPeriodDisplay(frequency)}
              </span>
              {assignedTo && entitlementId && (
                <>
                  <span className="text-sm-regular text-text-tertiary">•</span>
                  <span className="text-sm-regular text-text-tertiary">
                    {assignedTo}
                  </span>
                </>
              )}
            </div>
          </div>

          <Dropdown
            align="end"
            menu={{
              groups: [
                {
                  items: dropdownItems,
                },
              ],
            }}
          >
            <Button variant="tertiary" size="sm" className="bg-primary-fg">
              <Icons.DotsVertical className="h-5 w-5 text-text-tertiary" />
            </Button>
          </Dropdown>
        </div>
      </div>

      <PlanModal
        isOpen={isModalOpen}
        onClose={closeModal}
        initialStep={modalStep}
        name={name}
        category={category}
        price={price}
        billingCycle="year"
        nextPaymentDue={nextPaymentDue}
        features={features}
        planDetails={planDetails}
        onCancelPlan={
          companyId && subscriptionId ? undefined : handleConfirmCancellation
        }
        assignedTo={assignedTo}
        entitlementIndex={entitlementIndex}
        totalEntitlements={totalEntitlements}
        originalProductId={originalProductId}
        companyId={companyId}
        subscriptionId={subscriptionId}
        productId={cleanId}
        onPlanDeleted={onPlanDeleted}
      />
    </>
  );
};

export default PlanCard;
